/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import dayjs from 'dayjs';

import { ListeTable } from '@mairistem/liste';

import COLORS from '@/constants/colors';
import TYPES_ACTES from '@/constants/typesActes';
import { getActeListeColumns } from '@/constants';
import { EllipsisText } from '@/components/Common';

interface ActeListeTableRowProps {
  item: any;
  invalidate?: () => void;
  mode?: string;
  dashboard?: boolean;
  typeActe?: string;
  selected?: boolean;
}

const ActeListeTableRow = (props: ActeListeTableRowProps) => {
  const { item, invalidate, mode, dashboard, typeActe, selected } = props;

  // biome-ignore lint/correctness/useExhaustiveDependencies: invalidate must only re-run on mode change
  React.useEffect(() => {
    invalidate?.();
  }, [mode]);

  const nomClassement = item?.nom_classement;
  const nomClassement2 = item?.nom_classement_2;
  const nomClassementPere = item?.nom_classement_pere;
  const nomClassementMere = item?.nom_classement_mere;
  const sexe1 = item?.sexe_1;
  const sexe2 = item?.sexe_2;
  const itemTypeActe = item?.type_acte;
  const dateActe = item?.date_acte;
  const numero = item?.numero;
  const numeroRegistre = item?.numero_registre;
  const libelleRegistre = item?.libelle_registre;

  const { nomLabel, parentLabels } = getActeListeColumns(typeActe);
  // La reconnaissance affiche à la fois le nom (l'enfant) et les 2 parents :
  // les colonnes "parent" utilisent alors nom_classement_pere/_mere (rôles
  // fixes), pas nom_classement/_2 (déjà pris par la colonne "nom").
  const showsBothNomAndParents = !!nomLabel && !!parentLabels;

  const getCivility = React.useCallback((sexe: number | null): string => {
    if (sexe === 1) return 'M.';
    if (sexe === 2) return 'Mme';
    return '';
  }, []);

  const renderDashboardRow = React.useMemo(() => {
    const nom1 = nomClassement
      ? `${getCivility(sexe1)} ${nomClassement}`.trim()
      : null;
    const nom2 = nomClassement2
      ? `${getCivility(sexe2)} ${nomClassement2}`.trim()
      : null;
    const displayNom =
      nom1 && nom2 ? `${nom1} et ${nom2}` : nom1 || 'Non renseigné';

    const typeActeConfig = TYPES_ACTES.find((t) => t.key === itemTypeActe);
    const typeActeLabel = itemTypeActe
      ? itemTypeActe.charAt(0).toUpperCase() + itemTypeActe.slice(1)
      : '-';

    return (
      <>
        <ListeTable.Cell name="nomClassement">{displayNom}</ListeTable.Cell>
        <ListeTable.Cell name="typeActe">
          <span
            style={{
              backgroundColor:
                typeActeConfig?.statsColor ?? COLORS.GreyDashboardColor,
              borderRadius: '4px',
              padding: '5px',
              fontWeight: 600,
              fontSize: '0.90em',
              display: 'inline-block',
              width: '10em',
              textAlign: 'center',
            }}
          >
            {typeActeLabel}
          </span>
        </ListeTable.Cell>
      </>
    );
  }, [nomClassement, nomClassement2, sexe1, sexe2, itemTypeActe, getCivility]);

  const renderStatsRow = React.useMemo(() => {
    const formattedDate = dateActe ? dayjs(dateActe).format('DD/MM/YYYY') : '-';
    const registre =
      numeroRegistre || libelleRegistre
        ? `${numeroRegistre} - ${libelleRegistre?.toUpperCase() ?? '-'}`
        : '-';

    const nomClassementPereLabel = nomClassementPere || 'Non renseigné';
    const nomClassementMereLabel = nomClassementMere || 'Non renseigné';
    const partenaire1Label = nomClassement
      ? `${getCivility(sexe1)} ${nomClassement}`.trim()
      : 'Non renseigné';
    const partenaire2Label = nomClassement2
      ? `${getCivility(sexe2)} ${nomClassement2}`.trim()
      : 'Non renseigné';

    return (
      <>
        <ListeTable.Cell name="numero">{numero || '-'}</ListeTable.Cell>
        <ListeTable.Cell name="registre">
          <EllipsisText text={registre} />
        </ListeTable.Cell>
        <ListeTable.Cell name="dateActe">{formattedDate}</ListeTable.Cell>
        {nomLabel && (
          <ListeTable.Cell name="nomClassement">
            <EllipsisText text={nomClassement || 'Non renseigné'} />
          </ListeTable.Cell>
        )}
        {parentLabels &&
          (showsBothNomAndParents ? (
            <>
              <ListeTable.Cell name="partenaire1">
                <EllipsisText text={nomClassementPereLabel} />
              </ListeTable.Cell>
              <ListeTable.Cell name="partenaire2">
                <EllipsisText text={nomClassementMereLabel} />
              </ListeTable.Cell>
            </>
          ) : (
            <>
              <ListeTable.Cell name="partenaire1">
                <EllipsisText text={partenaire1Label} />
              </ListeTable.Cell>
              <ListeTable.Cell name="partenaire2">
                <EllipsisText text={partenaire2Label} />
              </ListeTable.Cell>
            </>
          ))}
      </>
    );
  }, [
    nomClassement,
    nomClassement2,
    nomClassementPere,
    nomClassementMere,
    sexe1,
    sexe2,
    dateActe,
    numero,
    numeroRegistre,
    libelleRegistre,
    nomLabel,
    parentLabels,
    showsBothNomAndParents,
    getCivility,
  ]);

  if (!item) return null;

  return (
    <ListeTable.Row key={item.identifiant ?? item?.id} selected={selected}>
      {dashboard ? renderDashboardRow : renderStatsRow}
    </ListeTable.Row>
  );
};

export default ActeListeTableRow;
